import { Field } from '@atlaskit/form';
import { Box, Text, xcss } from '@atlaskit/primitives';
import { EditableList, type EditableListProps } from '@databases/editable-list';
import { defaultStoreUtils } from '@databases/redux';
import { addChangeToDraft } from '@databases/redux/changesets/actions';
import {
  type DatabaseField,
  type DatabaseFieldType,
  type SelectOption
} from '@databases/types/database';
import { DatabaseChangeType } from '@databases/types/redux';
import {
  MultiValueChangeType,
  type SelectOptionsChange
} from '@databases/utils/database/changesets';
import { getNewOption } from '@databases/utils/database/field-types/select';
import React, { type FunctionComponent, useCallback, useEffect, useRef } from 'react';
import { useIntl } from 'react-intl-next';

import { messages } from './messages.ts';
import SelectOptionEdit from './shared/SelectOptionEdit.tsx';

const listStyles = xcss({
  marginInlineStart: 'space.negative.200',
  marginInlineEnd: 'space.negative.100'
});

const dividerStyles = xcss({
  borderBlockStartColor: 'color.border',
  borderBlockStartStyle: 'solid',
  borderBlockStartWidth: 'border.width',
  marginInline: 'space.negative.200',
  marginBlockStart: 'space.150',
  paddingBlockEnd: 'space.075'
});

const headerStyles = xcss({
  paddingBlockEnd: 'space.050'
});

type TagListProps = EditableListProps<SelectOption, SelectOptionsChange>;

const getId = (option: SelectOption): string => option.id ?? option.label;

type SelectFieldSettingsProps = {
  contentId: string;
  field: DatabaseField<DatabaseFieldType.SELECT>;
};

const SelectFieldSettings: FunctionComponent<SelectFieldSettingsProps> = ({
  contentId,
  field
}) => {
  const { formatMessage } = useIntl();
  const dispatch = defaultStoreUtils.useDispatch();
  const options = field.settings.selectOptions;

  const dispatchChange = useCallback(
    (change: SelectOptionsChange, newValue: SelectOption[]) => {
      dispatch(
        addChangeToDraft(
          contentId,
          {
            type: DatabaseChangeType.SET_SELECT_OPTIONS,
            fieldId: field.id,
            change,
            newValue
          },
          {
            fieldSettingsAnalytics: {
              source: 'fieldContextualMenu',
              fieldType: field.type
            }
          }
        )
      );
    },
    [contentId, dispatch, field.id, field.type]
  );

  const handleItemChange = useCallback<NonNullable<TagListProps['onItemChange']>>(
    (_option, _index, change, allOptions) => {
      dispatchChange(change, allOptions);
    },
    [dispatchChange]
  );

  const handleAddNewItem = useCallback<NonNullable<TagListProps['onAddNewItem']>>(
    (option, allOptions) => {
      dispatchChange({ type: MultiValueChangeType.APPEND, value: option }, allOptions);
    },
    [dispatchChange]
  );

  const handleRemoveItem = useCallback<NonNullable<TagListProps['onRemoveItem']>>(
    (_option, index, allOptions) => {
      dispatchChange({ type: MultiValueChangeType.REMOVE, index }, allOptions);
    },
    [dispatchChange]
  );

  const handleReorder = useCallback<NonNullable<TagListProps['onReorder']>>(
    (_option, currentIndex, newIndex, allOptions) => {
      dispatchChange(
        { type: MultiValueChangeType.REORDER, currentIndex, newIndex },
        allOptions
      );
    },
    [dispatchChange]
  );

  const getNewValue = useCallback(() => getNewOption(options), [options]);

  const renderField = useCallback<TagListProps['renderField']>(
    ({ autoFocus, i, isDisabled, onChange, value }) => (
      <SelectOptionEdit
        index={i}
        isDisabled={isDisabled}
        isDuplicateLabel={(label) =>
          options.some((other, index) => index !== i && other.label === label)
        }
        onChange={onChange}
        shouldAutoFocus={autoFocus}
        value={value}
      />
    ),
    [options]
  );

  const optionsRef = useRef(options);
  optionsRef.current = options;
  useEffect(
    () => () => {
      let remaining = [...optionsRef.current];
      optionsRef.current
        .flatMap((option, index) => (option.label.trim() === '' ? [index] : []))
        .reverse()
        .forEach((index) => {
          remaining = remaining.filter((_, i) => i !== index);
          dispatchChange({ type: MultiValueChangeType.REMOVE, index }, remaining);
        });
    },
    [dispatchChange]
  );

  return (
    <>
      <Field
        label={
          <Box xcss={headerStyles}>
            <Text size="small" weight="bold" color="color.text.subtle">
              {formatMessage(messages.tagOrderSectionLabel)}
            </Text>
          </Box>
        }
        name="select-options-field"
      >
        {() => (
          <Box xcss={listStyles}>
            <EditableList<SelectOption, SelectOptionsChange>
              addLabel={formatMessage(messages.tagOrderAddTagLabel)}
              addButtonIconSize="small"
              addButtonTextSize="small"
              addButtonTextWeight="regular"
              compactAddButton
              compactDeleteButton
              compactListPadding
              deleteTooltip={formatMessage(messages.tagOrderDeleteTagTooltip)}
              getId={getId}
              getNewValue={getNewValue}
              onAddNewItem={handleAddNewItem}
              onItemChange={handleItemChange}
              onRemoveItem={handleRemoveItem}
              onReorder={handleReorder}
              renderField={renderField}
              values={options}
              verticalScroll
            />
          </Box>
        )}
      </Field>
      <Box role="separator" xcss={dividerStyles} />
    </>
  );
};

export default SelectFieldSettings;
